import { Alert } from 'src/design/atoms/Alert';
import { Box } from 'src/design/atoms/Box';
import { Grid } from 'src/design/atoms/Grid';
import { Group } from 'src/design/atoms/Group';
import { Text } from 'src/design/atoms/Text';
import { TextInput } from 'src/design/atoms/TextInput';
import React from 'react';
import { Rate, RateRequest } from 'src/api/FiretrolApiClient';
import {
  useCreateJobRate,
  useRemoveJobRate,
} from 'src/api/queries/rates/useJobRateMutations';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { DeleteButton } from 'src/design/atoms/DeleteButton';
import { getErrorMessage } from 'src/utils';
import { DraggableItem } from './DraggableItem';
import { StaticItem } from './StaticItem';

export interface IRateSheetSectionProps {
  category: string;
  measurement: string;
  title: string;
  jobId: number;
  rates: Rate[];
  interactable: boolean;
}
const initialValues: RateRequest = {
  label: '',
  rate1: '',
  rateMeasurement: '',
  category: '',
  quantity: '',
};

export const RateSheetSection: React.FC<IRateSheetSectionProps> = ({
  rates,
  interactable,
  title,
  category,
  measurement,
  jobId,
}) => {
  const createRate = useCreateJobRate(jobId);
  const removeRate = useRemoveJobRate(jobId);
  const [showForm, setShowForm] = React.useState(false);
  const [values, setValues] = React.useState<RateRequest>(initialValues);
  const setFieldValue = (field: 'label' | 'rate1', value: string) => {
    setValues((prev) => {
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const resetForm = () => {
    setValues(initialValues);
    setShowForm(false);
  };

  const enterCreateMode = () => {
    setValues({
      category,
      rateMeasurement: measurement,
      label: '',
      rate1: '',
      quantity: '',
    });
    setShowForm(true);
  };

  const submitRate = async () => {
    const result = await createRate.mutateAsync(values).catch(() => null);
    if (result) resetForm();
  };

  const remove = (rate: Rate) => {
    removeRate.mutate(rate.id);
  };

  const renderLines = () => {
    if (rates.length === 0) {
      return (
        <Alert color="gray" title={`No ${title} Rates`} variant="light">
          {/* Empty state */}
        </Alert>
      );
    }
    return rates.map((rate) => {
      if (interactable) {
        return (
          <DraggableItem
            key={rate.id}
            rate={rate}
            onRemove={() => remove(rate)}
          />
        );
      }
      return (
        <StaticItem key={rate.id} rate={rate} onRemove={() => remove(rate)} />
      );
    });
  };
  return (
    <>
      <Grid mb="md">
        <Grid.Col span={6}>
          <Box component="h4" mb={0}>
            {title}
          </Box>
        </Grid.Col>
        <Grid.Col span={3}>
          {!interactable && (
            <Box component="h4" mb={0} pr="md">
              Rate
            </Box>
          )}
        </Grid.Col>
        <Grid.Col span={3} />
      </Grid>
      <Box mb="md">{renderLines()}</Box>

      {!interactable && (
        <>
          {showForm ? (
            <>
              {createRate.error && <p>{getErrorMessage(createRate.error)}</p>}
              <Grid>
                <Grid.Col span={5} pr={0}>
                  <TextInput
                    value={values.label || ''}
                    onChange={(e) => setFieldValue('label', e.target.value)}
                    placeholder={title}
                  />
                </Grid.Col>
                <Grid.Col span={3} pl="xs" pr={0}>
                  <TextInput
                    value={values.rate1 || ''}
                    onChange={(e) => {
                      if (
                        isNaN(Number(e.target.value)) &&
                        e.target.value !== ''
                      )
                        return;
                      setFieldValue('rate1', e.target.value);
                    }}
                    placeholder="Rate"
                    rightSection={
                      <Text size="sm" c="dimmed">
                        /{measurement}
                      </Text>
                    }
                  />
                </Grid.Col>
                <Grid.Col span="auto" ta="right" pl={0}>
                  <Group gap="sm">
                    <CreateButton
                      loading={createRate.isPending}
                      onClick={submitRate}
                    >
                      Add
                    </CreateButton>
                    <DeleteButton onClick={resetForm}>Remove</DeleteButton>
                  </Group>
                </Grid.Col>
              </Grid>
            </>
          ) : (
            <BasicButton
              variant="subtle"
              color="blue"
              fullWidth
              onClick={enterCreateMode}
            >
              Add {title}
            </BasicButton>
          )}
        </>
      )}
    </>
  );
};
